import { useIonRouter, UseIonRouterResult } from '@ionic/react'
import { buildTrainingUrl } from '@shared/hooks/trainingsNavigation/base'
import { Training } from '@entities/training'
import { workoutExecutionStore } from '@features/workout/execution/workoutExecutionStore'
import { WorkoutHistoryProxy } from '@entities/training/api/historyProxy'
import { StartHistoryResult } from '@entities/training/types/history'
import { LocalStorageKeys } from '@shared/constants/LocalStorageKeys'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { resetWorkoutExecutionElapsed } from '@pages/exercise/lib/workoutCompletionSnapshot'

const getTrainingReturnUrl = (
	redirectUrl: string | undefined,
	location: ReturnType<typeof useLocation>
) => redirectUrl || `${location.pathname}${location.search}${location.hash}`

const getResumeExerciseOrder = (
	completedExercises: number | undefined,
	exercisesAmount: number
) => {
	const nextExerciseOrder = (completedExercises ?? 0) + 1

	return Math.min(Math.max(nextExerciseOrder, 1), Math.max(exercisesAmount, 1))
}

export const useStartTraining = (training: Training, redirectUrl?: string) => {
	const nav: UseIonRouterResult = useIonRouter()
	const location = useLocation()
	const workoutHistoryId: number | undefined = training.history?.id_workout_history
	const isStartingRef = useRef(false)

	return async () => {
		if (isStartingRef.current) {
			return
		}
		isStartingRef.current = true
		localStorage.setItem(
			LocalStorageKeys.workoutRedirectURL,
			getTrainingReturnUrl(redirectUrl, location)
		)
		workoutExecutionStore.setExercises(training.exercises)
		if (!workoutHistoryId) {
			const result: StartHistoryResult | null = await WorkoutHistoryProxy.startTraining(
				training.id_workout
			)
			if (!result) {
				isStartingRef.current = false
				return
			}
			resetWorkoutExecutionElapsed(result.id_workout_history)
			nav.push(buildTrainingUrl(result.id_workout_history, 1, 'Training'))
			return
		}

		const exerciseOrder = getResumeExerciseOrder(
			training.history?.exercises_completed,
			training.exercises.length
		)

		nav.push(
			buildTrainingUrl(workoutHistoryId, exerciseOrder, 'Training')
		)
	}
}

export const useStartTrainingDeferred = () => {
	const [trainingData, setTrainingData] = useState<{
		training: Training | null
		redirectUrl: string | null
	}>({ training: null, redirectUrl: null })
	const nav: UseIonRouterResult = useIonRouter()
	const location = useLocation()
	const startedRequestKeyRef = useRef<string | null>(null)

	const triggerTraining = (training: Training, redirectUrl?: string) => {
		startedRequestKeyRef.current = null
		setTrainingData({ training, redirectUrl: getTrainingReturnUrl(redirectUrl, location) })
	}

	useEffect(() => {
		if (!trainingData.training || !trainingData.redirectUrl) return
		const training = trainingData.training
		const redirectUrl = trainingData.redirectUrl
		const workoutHistoryId: number | undefined = training.history?.id_workout_history
		const requestKey = `${workoutHistoryId ?? `new_${training.id_workout}`}:${redirectUrl}`

		if (startedRequestKeyRef.current === requestKey) {
			return
		}
		startedRequestKeyRef.current = requestKey
		setTrainingData({ training: null, redirectUrl: null })

		const startTraining = async () => {
			localStorage.setItem(LocalStorageKeys.workoutRedirectURL, redirectUrl)
			workoutExecutionStore.setExercises(training.exercises)

			if (!workoutHistoryId) {
				const result: StartHistoryResult | null = await WorkoutHistoryProxy.startTraining(
					training.id_workout
				)
				if (!result) {
					return
				}
				resetWorkoutExecutionElapsed(result.id_workout_history)
				nav.push(buildTrainingUrl(result.id_workout_history, 1, 'Training'))
				return
			}

			const exerciseOrder = getResumeExerciseOrder(
				training.history?.exercises_completed,
				training.exercises.length
			)

			nav.push(
				buildTrainingUrl(workoutHistoryId, exerciseOrder, 'Training')
			)
		}

		startTraining()
	}, [trainingData, nav])

	return triggerTraining
}
